import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import cn from 'bem-cn';
import { isUndefined } from 'lodash';

import trophy from '../../images/trophy.png';
import radialBackground from '../../images/radial.svg';
import { RoutePaths } from '../../constants';

import { useLocalStorageForm } from '../NewPromptPage/NewPromptPage.hooks';
import { ScreenSize, useGetScreenSize, useHover, useNavigateBack } from '../../hooks';
import { useUserContext, useModal } from '../../contexts';
import { useSetUserAgreementMutation } from '../../store/services';
import { ShareMethod, shareUrl } from '../../utils/share';

import { WebAppLayout } from '../../layouts/WebAppLayout';
import { HeaderElements } from '../../components/Header';
import { NavigationElements } from '../../components/Navigation';
import {
    Stack,
    Tabs,
    InfoBlock,
    Link,
    ImageWithFadeIn,
    Banner,
    Slider,
    Icon,
    Popover,
    Toggle,
    Skeleton,
} from '../../components/UI';
import { TabItem } from '../../components/UI/Tabs/Tabs.types';
import { Profile } from '../../components/Profile/Profile';

import './MyProfilePage.scss';

const b = cn('my-profile-page');

const tabItems: TabItem[] = [
    {
        text: 'Промпты',
        icon: 'prompt',
        to: RoutePaths.ProfilePrompts,
    },
    {
        text: 'Рейтинг',
        icon: 'rating',
        to: RoutePaths.ProfileRating,
    },
    {
        text: 'Брелоки',
        icon: 'crown',
        to: RoutePaths.ProfileAchievements,
    },
];

export const MyProfilePage: React.FC = () => {
    const screenSize = useGetScreenSize();
    const isTouch = screenSize < ScreenSize.Tablet;

    const { user } = useUserContext();
    const isProfilePublic = Boolean(user?.isPublicProfile);

    const { howToEarnPoints, profilePublicity } = useModal();
    const handleBackButtonClick = useNavigateBack();

    const { formData } = useLocalStorageForm();
    const isDraftExists = formData.promptText || formData.step > 1;

    const [setUserAgreement, { isLoading: isUserAgreementSending }] = useSetUserAgreementMutation();

    const {
        isHovered: isShareButtonHovered,
        onMouseEnter: onShareButtonMouseEnter,
        onMouseLeave: onShareButtonMouseLeave,
    } = useHover();
    const togglePopoverText = isProfilePublic ? 'Ваш профиль виден всем' : 'Сделайте профиль публичным';
    const [isTogglePopoverOpen, setIsTogglePopoverOpen] = useState(false);

    const handleToggleChange = (newValue: boolean) => {
        setUserAgreement({ isPublic: newValue });

        if (newValue) {
            // Показываем тултип "Ваш профиль виден всем" при включении тогла
            setIsTogglePopoverOpen(true);
            setTimeout(() => setIsTogglePopoverOpen(false), 2000);
        }
    };

    const [headerShareButtonPopoverOpen, setHeaderShareButtonPopoverOpen] = useState(false);

    const shareProfile = async () => {
        const loginOrPublicId = user.login || user.publicId;

        if (!loginOrPublicId) {
            return;
        }

        return await shareUrl({
            title: 'Промптхаб — платформа обмена идеями, как использовать Алису AI и другие нейросети',
            text: 'Мой профиль в Промптхабе',
            url: `https://${window.location.hostname}/prompthub/users/${loginOrPublicId}`,
        });
    };

    const handleProfileShareButtonClick = async () => {
        if (!isProfilePublic) {
            return;
        }

        shareProfile();
    };

    const handleHeaderShareButtonClick = async () => {
        if (!isProfilePublic) {
            // Показываем тултип "Сделайте профиль публичным" при клике на кнопку "Поделиться" в шапке
            setIsTogglePopoverOpen(true);
            setTimeout(() => {
                setIsTogglePopoverOpen(false);
            }, 2000);

            return;
        }

        const shareResult = await shareProfile();
        if (shareResult?.method === ShareMethod.Clipboard) {
            setHeaderShareButtonPopoverOpen(true);
            setTimeout(() => {
                setHeaderShareButtonPopoverOpen(false);
            }, 2000);
        }
    };

    // Показываем тултип "Сделайте профиль публичным" при наведении на кнопку "Поделиться"
    useEffect(() => {
        if (!isProfilePublic) {
            setIsTogglePopoverOpen(isShareButtonHovered);
        }
    }, [isProfilePublic, isShareButtonHovered]);

    const headerElements: HeaderElements = {
        desktop: {
            left: {
                type: 'logo',
            },
            right: {
                type: 'profile',
            },
        },
        mobile: {
            left: {
                type: 'backButton',
                onClick: handleBackButtonClick,
            },
            center: {
                type: 'custom',
                render: () => {
                    return <span className={b('header-text')}>ИИ профиль</span>;
                },
            },
            right: {
                type: 'custom',
                render: () => {
                    return (
                        <Popover
                            content="Ссылка скопирована"
                            position="bottom-left"
                            open={headerShareButtonPopoverOpen}
                            contentClassName="nowrap"
                        >
                            <Icon name="share" size={24} onClick={handleHeaderShareButtonClick} />
                        </Popover>
                    );
                },
            },
        },
    };

    const emptyNavigationElements: NavigationElements = {
        desktop: [],
        mobile: [],
    };

    return (
        <WebAppLayout headerElements={headerElements} navigationElements={emptyNavigationElements}>
            <div className={b('background')}>
                <ImageWithFadeIn src={radialBackground} alt="" />
            </div>
            <Stack spacing={24}>
                <Stack spacing={12}>
                    <Profile
                        isMy
                        avatarSrc={user.avatarSrc}
                        firstName={user.firstName}
                        lastName={user.lastName}
                        isProfilePublic={isProfilePublic}
                        onShareButtonMouseEnter={onShareButtonMouseEnter}
                        onShareButtonMouseLeave={onShareButtonMouseLeave}
                        onShareButtonClick={handleProfileShareButtonClick}
                    />
                    <Stack spacing={8} direction="row" justifyContent="center" className={b('publicity')}>
                        <Icon name="info" size={24} onClick={profilePublicity.open} />
                        <Stack as="label" spacing={8} direction="row">
                            Публичный профиль
                            {isUndefined(isProfilePublic) ? (
                                <Skeleton height={24} width={40} style={{ borderRadius: '12px' }} />
                            ) : (
                                <Popover
                                    position={isTouch ? 'bottom-left' : 'bottom'}
                                    content={togglePopoverText}
                                    open={isTogglePopoverOpen}
                                    contentClassName="nowrap"
                                >
                                    <Toggle
                                        checked={isProfilePublic}
                                        onChange={handleToggleChange}
                                        disabled={isUserAgreementSending}
                                    />
                                </Popover>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
                <Slider className={b('slider')}>
                    {isDraftExists && (
                        <Link to={RoutePaths.NewPrompt} className={b('draft-info-block')}>
                            <InfoBlock text="Продолжить заполнение промпта" />
                        </Link>
                    )}
                    <Banner
                        text="Узнайте, как получить баллы и подняться в рейтинге"
                        image={trophy}
                        onClick={howToEarnPoints.open}
                    />
                </Slider>
                <div className={b('tabs')}>
                    <Tabs items={tabItems} />
                </div>
                <div className={b('content')}>
                    <Outlet />
                </div>
            </Stack>
        </WebAppLayout>
    );
};
